import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, memo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { isEmptyString } from '../../../utils';
import { Popup } from '../../Popup';

import classes from './EditorStats.module.scss';

const WORDS_PER_MINUTE = 200;

interface EditorStatsProps {
  editor: Editor;
}

export const EditorStats: FunctionComponent<EditorStatsProps> = memo(({ editor }) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'richTextEditor' });

  const { words, characters, paragraphs } = useEditorState({
    editor,
    selector: ({ editor: { state, storage } }) => ({
      words: storage.characterCount.words(),
      characters: storage.characterCount.characters(),
      paragraphs: state.doc.children.filter(
        ({ type, textContent }) => type.name === 'paragraph' && !isEmptyString(textContent)
      ).length,
    }),
  });

  const [isOpen, setIsOpen] = useState(false);

  const readingTime = ctxT(words < WORDS_PER_MINUTE ? 'stats-under-one-minute' : 'stats-minutes', {
    count: Math.ceil(words / WORDS_PER_MINUTE),
  });

  return (
    <Popup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      body={
        <dl className={classes['editor-stats__list']}>
          <dt>{ctxT('stats-words')}</dt>
          <dd>{words}</dd>
          <dt>{ctxT('stats-characters')}</dt>
          <dd>{characters}</dd>
          <dt>{ctxT('stats-paragraphs')}</dt>
          <dd>{paragraphs}</dd>
          <dt>{ctxT('stats-reading-time')}</dt>
          <dd>{readingTime}</dd>
        </dl>
      }
      bodyBottom={'calc(100% + 0.5rem)'}
      bodyLeft={'0'}
      bodyClassName={classes['editor-stats__popover']}
      iconId={'info'}
      iconSize={16}
      ariaLabel={ctxT('stats-summary', { count: words, readingTime })}
      labelFirst
      color={'#6d767c'}
      hoverColor={'#43445b'}
      parentClassName={classes['editor-stats']}
      className={classes['editor-stats__trigger']}
      labelClassName={classes['editor-stats__summary']}
      {...(!isMobileOnly && { label: ctxT('stats-summary', { count: words, readingTime }) })}
    />
  );
});
